import { classifySyncError } from "../domain/classifySyncError";
import { pickReadyOutbox, type OutboxItem } from "../domain/outboxScheduler";
import { newClientId } from "../domain/conflicts";
import type { CareBundle, OfflineStore } from "../store/types";

export type SyncResult = {
  success: number;
  failed: number;
  conflicts: number;
  quarantined: number;
  errors: { id: string; error: string }[];
  /** Der Bundle-Abruf ist fehlgeschlagen. Der Versand läuft davon unabhängig. */
  pullFailed: boolean;
};

export type OutboxRpc = {
  submitCareEntry: (item: OutboxItem) => Promise<unknown>;
  createCase: (item: OutboxItem) => Promise<unknown>;
  createCasesBatch: (item: OutboxItem) => Promise<unknown>;
  attachMedia: (item: OutboxItem) => Promise<unknown>;
  pullBundle: (since: Record<string, string>, knownCaseIds: string[]) => Promise<CareBundle>;
};

const CONFLICT_NOTICE: Record<string, string> = {
  occurrence_conflict:
    "Diese reguläre Gabe ist serverseitig bereits erledigt. Bitte verwerfen oder als Zusatzgabe mit Begründung einreichen.",
  place_conflict:
    "Der Unterbringungsplatz hat sich geändert. Der lokale Eintrag bleibt erhalten.",
  diagnosis_conflict:
    "Der Diagnosestand hat sich geändert. Der lokale Eintrag bleibt erhalten.",
  domain_conflict: "Fachlicher Konflikt beim Synchronisieren. Der lokale Eintrag bleibt erhalten.",
};

export async function applyPulledBundle(
  store: OfflineStore,
  bundle: CareBundle
): Promise<void> {
  const evicted = (bundle.evicted_case_ids ?? []).map(String);
  if (evicted.length) {
    await store.evictCases(
      evicted,
      "Fall gehört nicht mehr zum Offline-Bereich (Übergabe, Abschluss oder Rechteänderung). Ungesendete Einträge sind in Quarantäne."
    );
  }
  await store.setBundle(bundle);
  const siteIds = (bundle.site_ids ?? []).map(String);
  if (siteIds.length) await store.purgeSitesNotIn(siteIds);
}

export async function drainOutbox(
  store: OfflineStore,
  rpc: Pick<
    OutboxRpc,
    "submitCareEntry" | "createCase" | "createCasesBatch" | "attachMedia"
  >
): Promise<SyncResult> {
  const result: SyncResult = {
    success: 0,
    failed: 0,
    conflicts: 0,
    quarantined: 0,
    errors: [],
    pullFailed: false,
  };

  // Repeat until no newly ready independent items remain (dependency-aware, not global FIFO).
  for (let pass = 0; pass < 20; pass += 1) {
    const items = await store.getOutbox();
    const ready = pickReadyOutbox(items);
    if (ready.length === 0) break;
    let progressed = false;

    for (const item of ready) {
      try {
        if (item.op === "submit_care_entry") await rpc.submitCareEntry(item);
        else if (item.op === "create_animal_case") await rpc.createCase(item);
        else if (item.op === "create_animal_cases_batch") await rpc.createCasesBatch(item);
        else await rpc.attachMedia(item);

        const synced: OutboxItem = {
          ...item,
          status: "synced",
          error: null,
          conflictKind: null,
          updatedAt: new Date().toISOString(),
        };
        await store.upsertOutbox(synced);
        if (item.op === "submit_care_entry") {
          await store.appendLocalEntry({
            id: String(item.payload.serverEntryId ?? item.clientId),
            case_id: item.caseId ?? "",
            client_entry_id: item.clientId,
            recorded_at: String(item.payload.recordedAt ?? item.createdAt),
            received_at: new Date().toISOString(),
            author_id: null,
            events: (item.payload.events as unknown[]) ?? [],
            syncStatus: "synced",
          });
        }
        result.success += 1;
        progressed = true;
      } catch (error) {
        const kind = classifySyncError(error, {
          op: item.op,
          clientId: item.clientId,
        });
        if (kind === "idempotent_success") {
          await store.upsertOutbox({
            ...item,
            status: "synced",
            error: null,
            conflictKind: null,
            updatedAt: new Date().toISOString(),
          });
          result.success += 1;
          progressed = true;
          continue;
        }
        if (
          kind === "occurrence_conflict" ||
          kind === "place_conflict" ||
          kind === "diagnosis_conflict" ||
          kind === "domain_conflict"
        ) {
          const conflictKind =
            kind === "occurrence_conflict"
              ? "occurrence"
              : kind === "place_conflict"
                ? "place"
                : kind === "diagnosis_conflict"
                  ? "diagnosis"
                  : "domain";
          await store.upsertOutbox({
            ...item,
            status: "conflict",
            conflictKind,
            error: error instanceof Error ? error.message : String(error),
            updatedAt: new Date().toISOString(),
          });
          await store.upsertConflict({
            id: item.id,
            outboxId: item.id,
            kind: conflictKind,
            caseId: item.caseId,
            payload: { ...item.payload },
            serverHint: CONFLICT_NOTICE[kind] ?? null,
            createdAt: item.createdAt,
          });
          await store.addNotice(CONFLICT_NOTICE[kind] ?? "Synchronisierungskonflikt.");
          result.conflicts += 1;
          result.failed += 1;
          result.errors.push({
            id: item.id,
            error: error instanceof Error ? error.message : String(error),
          });
          continue;
        }
        if (kind === "quarantine") {
          await store.upsertOutbox({
            ...item,
            status: "quarantine",
            error: error instanceof Error ? error.message : String(error),
            updatedAt: new Date().toISOString(),
          });
          await store.addNotice(
            "Keine Schreibrechte mehr für diesen Vorgang. Der Eintrag bleibt in Quarantäne und wurde nicht gelöscht."
          );
          result.quarantined += 1;
          result.failed += 1;
          result.errors.push({
            id: item.id,
            error: error instanceof Error ? error.message : String(error),
          });
          continue;
        }
        await store.upsertOutbox({
          ...item,
          status: "error",
          error: error instanceof Error ? error.message : String(error),
          updatedAt: new Date().toISOString(),
        });
        result.failed += 1;
        result.errors.push({
          id: item.id,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
    if (!progressed) break;
  }

  return result;
}

export async function syncOnce(
  store: OfflineStore,
  rpc: OutboxRpc
): Promise<SyncResult> {
  const known = await store.getKnownCaseIds();
  const bundle = await store.getBundle();
  const since = bundle.cursors ?? {};
  const pulled = await rpc.pullBundle(since, known);
  await applyPulledBundle(store, pulled);
  return drainOutbox(store, rpc);
}

export function newOutboxId(): string {
  return newClientId();
}
